import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, FileSignature, Network, Scale } from "lucide-react";

const JOURNEY = [
  ["01", "Build request raised", "Sponsor describes the change in plain language."],
  ["02", "Existing system mapped", "What already exists, what must change, what stays."],
  ["03", "Scope & budget estimated", "Effort band, budget band, risk shape."],
  ["04", "Ownership & dependencies", "Who owns the work; what depends on what."],
  ["05", "Advisory plan drafted", "Deterministic templated steps — not a model decision."],
  ["06", "Developer & CTO review", "Authority gates, not surveillance."],
  ["07", "dtool evidence attached", "Build, test, deploy hashes from delivery machinery."],
  ["08", "Release readiness gate", "Approved or held with reasons."],
  ["09", "KATLAS receipt signed", "Custody · Authority · Receipts."],
  ["10", "Sponsor sees the trail", "A safe, explainable delivery record."],
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="pt-6 pb-16 bc-rise">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="eyebrow eyebrow-signal mb-6" data-testid="hero-eyebrow">
              BuildCierge · Governed software delivery
            </p>
            <h1
              className="font-display text-[44px] sm:text-[58px] lg:text-[72px] leading-[1.02] tracking-tight text-[hsl(var(--bc-ink))]"
              data-testid="hero-headline"
            >
              Know what is built, what is changing, who owns it
              <span className="text-[hsl(var(--bc-signal))]"> and what proves </span>
              it was delivered.
            </h1>
            <p className="mt-7 max-w-2xl text-[17px] leading-[1.65] text-[hsl(var(--bc-ink-soft))]" data-testid="hero-subhead">
              BuildCierge helps sponsors and CTOs control AI-assisted software delivery through scoped build
              requests, ownership maps, budget bands, change approvals, dtool evidence and KATLAS receipts —
              without turning delivery governance into developer surveillance.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/sponsor" className="bc-btn" data-testid="cta-open-sponsor">
                Open sponsor view <ArrowUpRight size={16} />
              </Link>
              <Link to="/requests" className="bc-btn bc-btn-ghost" data-testid="cta-follow-request">
                Follow a change request
              </Link>
              <Link to="/diagnostics" className="text-xs eyebrow ml-1 hover:text-[hsl(var(--bc-ink))]" data-testid="cta-katlas-diagnostics">
                KATLAS diagnostics →
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="bc-card-flat p-7 bc-rise bc-rise-d2">
              <p className="eyebrow mb-4">The promise</p>
              <p className="font-display text-[22px] leading-snug text-[hsl(var(--bc-ink))]">
                AI may generate code. Developers review and ship.
                BuildCierge governs the change. KATLAS signs the receipt.
              </p>
              <div className="rule my-6" />
              <ul className="text-sm space-y-3 text-[hsl(var(--bc-ink-soft))]">
                <li className="flex gap-3"><span className="signal-dot mt-2" /> Scope, budget, ownership, change control</li>
                <li className="flex gap-3"><span className="signal-dot mt-2" /> dtool evidence underneath as delivery machinery</li>
                <li className="flex gap-3"><span className="signal-dot mt-2" /> Public-safe receipts at <span className="font-mono">/r/:hash</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* THE CASE */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-0 border-y border-[hsl(var(--border))]" data-testid="scenario-panel">
        <div className="p-10 border-b md:border-b-0 md:border-r border-[hsl(var(--border))] bc-rise bc-rise-d1">
          <p className="eyebrow mb-4">The case</p>
          <p className="font-display text-[26px] leading-snug text-[hsl(var(--bc-ink))]" data-testid="case-text">
            A sponsor wants to add a new AI-assisted feature, but needs to know what already exists,
            what must change, who owns the work and what it will cost.
          </p>
        </div>
        <div className="p-10 bc-rise bc-rise-d2">
          <p className="eyebrow eyebrow-signal mb-4">What BuildCierge does</p>
          <p className="font-display text-[26px] leading-snug text-[hsl(var(--bc-ink))]" data-testid="value-text">
            BuildCierge shows the sponsor, CTO and delivery team the scope, dependencies,
            authority gates, delivery evidence and receipt that prove the change was governed.
          </p>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="py-20" data-testid="journey-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="eyebrow mb-3">The benefit pathway</p>
            <h2 className="font-display text-[34px] sm:text-[40px] tracking-tight leading-[1.05]">
              Ten steps from build request to receipt.
            </h2>
          </div>
          <Link to="/requests" className="bc-btn-text hidden sm:inline-flex">Browse open requests →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[hsl(var(--border))] border border-[hsl(var(--border))] rounded-md overflow-hidden">
          {JOURNEY.map(([n, title, body]) => (
            <div
              key={n}
              className="bg-[hsl(var(--bc-paper-2))] p-6 hover:bg-[hsl(var(--bc-paper))] transition-colors"
              data-testid={`journey-step-${n}`}
            >
              <div className="bc-numeral text-[44px]">{n}</div>
              <div className="mt-4 text-[15px] font-medium text-[hsl(var(--bc-ink))]">{title}</div>
              <div className="mt-2 text-[13px] leading-[1.55] text-[hsl(var(--bc-muted))]">{body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CAR PANEL */}
      <section className="py-16 border-t border-[hsl(var(--border))]" data-testid="car-panel">
        <p className="eyebrow eyebrow-signal mb-3">CAR — the governance grammar</p>
        <h2 className="font-display text-[34px] sm:text-[40px] tracking-tight leading-[1.05]">
          Custody controls the evidence. Authority governs the action.
          <span className="text-[hsl(var(--bc-signal))]"> Receipts prove what happened.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          {[
            {
              icon: <ShieldCheck size={20} />,
              eyebrow: "Custody",
              text: "Who controls repositories, prompts, build artefacts, test results, secrets, environments and delivery evidence. Evidence stays at source.",
            },
            {
              icon: <Scale size={20} />,
              eyebrow: "Authority",
              text: "Who may request, scope, approve, merge, release, rollback, increase budget or change priority. AI does not approve, deploy or judge.",
            },
            {
              icon: <FileSignature size={20} />,
              eyebrow: "Receipts",
              text: "What proves the request, approval, evidence review, deployment, exception or rollback happened — without exposing raw logs or secrets.",
            },
          ].map((c) => (
            <div key={c.eyebrow} className="bc-card p-7">
              <div className="flex items-center gap-3 text-[hsl(var(--bc-signal))]">{c.icon}<span className="eyebrow eyebrow-signal">{c.eyebrow}</span></div>
              <p className="mt-4 text-[15px] leading-[1.6] text-[hsl(var(--bc-ink-soft))]">{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHO GETS WHAT */}
      <section className="py-16 border-t border-[hsl(var(--border))]">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="eyebrow mb-3">Stakeholders</p>
            <h2 className="font-display text-[34px] sm:text-[40px] tracking-tight leading-[1.05]">
              What each stakeholder gets.
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            ["Platform sponsor", "A clear view of what is being built, why it matters, what it may cost and what receipt proves delivery."],
            ["CTO", "A governed view of scope, dependencies, authority, technical risk and release readiness."],
            ["Developer", "A clear task boundary, evidence checklist, build context and review path — not a velocity scoreboard."],
            ["DevOps / delivery operator", "A reproducible dtool evidence path for build, test, deploy and rollback."],
            ["Finance / budget owner", "A visible estimate, approval point and scope-change evidence."],
            ["Client / external sponsor", "A public-safe delivery receipt showing what was approved and released — never raw logs or secrets."],
          ].map(([who, what]) => (
            <div key={who} className="bc-card-flat p-7">
              <div className="eyebrow mb-3">{who}</div>
              <p className="text-[15px] leading-[1.6] text-[hsl(var(--bc-ink))]">{what}</p>
            </div>
          ))}
        </div>
      </section>

      {/* KATLAS */}
      <section className="py-16 border-t border-[hsl(var(--border))]" data-testid="katlas-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <p className="eyebrow eyebrow-signal mb-3">Why KATLAS sits underneath</p>
            <h2 className="font-display text-[34px] sm:text-[40px] tracking-tight leading-[1.05]">
              KATLAS doesn't replace GitHub, dtool, CI/CD or developer judgement.
            </h2>
          </div>
          <div className="lg:col-span-6">
            <p className="text-[16px] leading-[1.7] text-[hsl(var(--bc-ink-soft))]">
              It governs the actions <em>around</em> delivery: who requested the change, who had authority,
              what evidence was provided, what was approved, what was released and what receipt proves it.
              Raw artefacts stay at source; only governance metadata travels.
            </p>
            <div className="rule my-6" />
            <div className="flex items-center gap-3 text-sm">
              <Network size={16} className="text-[hsl(var(--bc-signal))]" />
              <span className="font-mono">/v1/status</span>
              <span className="font-mono">/v1/receipts/sign</span>
              <span className="font-mono">/v1/receipts/verify</span>
            </div>
            <Link to="/diagnostics" className="bc-btn-text mt-6 inline-flex" data-testid="link-diagnostics-detail">
              View KATLAS diagnostics →
            </Link>
          </div>
        </div>
      </section>

      {/* AI SAFETY */}
      <section className="py-14 border-t border-[hsl(var(--border))]">
        <div className="bc-card p-10">
          <p className="eyebrow mb-3">AI boundary</p>
          <p className="font-display text-[28px] leading-snug">
            AI may suggest. Technical authority decides. KATLAS signs the governed action.
          </p>
          <p className="mt-4 text-sm text-[hsl(var(--bc-muted))] max-w-3xl">
            v1 uses deterministic templated suggestions only. No live model is called. AI cannot approve builds,
            releases, budgets, evidence, deployments or judge individual developer performance.
          </p>
        </div>
      </section>
    </div>
  );
}
